import React from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';

import { PBBIncentiveBanner } from 'src/payments/components/pbb-incentive-banner/pbb-incentive-banner';
import { PaymentSelectionProvider, TabValue } from 'src/checkout/components/sections/payment/shared/context';
import { InstorePaymentOptions } from './instore-options';
import { PaymentSummary } from '../shared/payment-summary';
import { PaymentTabs } from './payment-tabs';
import { OnlinePaymentOptions } from './online-options';
import { useSelections } from './use-selection';

const Selection = (): JSX.Element => {
  const {
    showPaymentBanner,
    tabOptions,
    onTabChange,
    hasOnlineAndOfflinePaymentOptions,
    expanded,
    paymentMethod,
    onlinePaymentOption,
    hasOtherOrderTypesWithPBBEnabled,
    tabValue,
    offlinePaymentOptionValues,
    showFeeBadgeInline,
  } = useSelections();

  return (
    <CheckoutLabelGrouping id='payment-delivery' data-testid='payment-section'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Payment</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        {showPaymentBanner && <PBBIncentiveBanner marginBottom='16px' />}
        <PaymentsSummaryContainer isTabView={hasOnlineAndOfflinePaymentOptions && expanded}>
          {expanded &&
            (hasOnlineAndOfflinePaymentOptions && tabOptions.length > 0 ? (
              // Show the tabbed view if there are online and offline payment options and the accordion is expanded
              <PaymentTabs
                tabOptions={tabOptions}
                selectedTab={tabValue}
                onTabChange={onTabChange}
                showFeeBadgeInline={showFeeBadgeInline}
              >
                {tabValue === TabValue.online ? <OnlinePaymentOptions /> : <InstorePaymentOptions />}
              </PaymentTabs>
            ) : (
              <>
                {offlinePaymentOptionValues.includes(paymentMethod) && <InstorePaymentOptions />}
                {(!!onlinePaymentOption || hasOtherOrderTypesWithPBBEnabled) && <OnlinePaymentOptions />}
              </>
            ))}
          {!expanded && !!onlinePaymentOption && <PaymentSummary />}
        </PaymentsSummaryContainer>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
};

// Wrap the observed component with the provider
export const PaymentSelection = (): JSX.Element => (
  <PaymentSelectionProvider>
    <ObserverSelection />
  </PaymentSelectionProvider>
);

const ObserverSelection = observer(Selection);

const PaymentsSummaryContainer = styled.div<{ isTabView?: boolean }>`
  ${({ theme: { spaces, colors }, isTabView }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 98px;
    padding: ${isTabView ? '0px' : spaces[5]}; // only when the tab view is active
    background: ${colors.white};
    border-radius: ${spaces[2]};
    max-width: 390px;
    border: 1px solid #d9d9d9;
    ${!isTabView && `gap: ${String(spaces[4])};`}
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.07);
  `}
`;
